import { pressureColor, teamAColor, teamBColor, useBuilderContext } from "@Context/game/builder/builder-context";
import { ScenarioActionType } from "@Enums/index";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import Chevron from "@Resources/game/builder/chevron.png";
import PlayerToken from "@Resources/game/builder/player-token.png";
import Star from "@Resources/game/builder/star.png";
import { Point } from "@Shared/types/ScenarioTypes";
import { Bezier } from "bezier-js";
import { useCallback, useEffect, useState } from "react";
import { addAction, getPlayer, getSelectedPlayer, updatePlayer } from "./functions/player-functions";
import { getAngleAtTime, getBeziers, getClampedPosition, getPointAtTime, lerp, stageToScreen } from "./functions/utilities";
import { Facing, Player as PlayerType, Score, TargetMode, Waypoint } from "./models/builder-data-types";
import PlayerAction from "./player-action";
import Route from "./route";

type ScoreWindow = Score & {
  endTime: number;
};

const Player = (props: {
  id: number;
  player: PlayerType;
  size?: number;
  selected?: boolean;
  time?: number;
  onClick?: () => void;
  onWaypointClick?: (index: number) => void;
}) => {
  const size = props.size || 32;
  const angleCacheResolution = 30;

  const defaultPoint = {
    position: { x: 100, y: 100 },
    prev: { x: 100, y: 100 },
    next: { x: 100, y: 100 },
    t: 0,
  };

  const { state, dispatch } = useBuilderContext();

  const [points, setPoints] = useState<Waypoint[]>(props.player.points || [defaultPoint]);
  const [pos, setPos] = useState<Point>(defaultPoint.position);
  const [selected, setSelected] = useState(props.selected || false);
  const [hovered, setHovered] = useState(false);
  const [teamColour, setTeamColour] = useState("white");
  const [beziers, setBeziers] = useState<Bezier[]>(props.player.beziers || []);
  const [facingAngles, setFacingAngles] = useState<Facing[]>([]);
  const [currentFacingAngle, setCurrentFacingAngle] = useState(0);
  const [lookAngles, setLookAngles] = useState<Facing[]>([]);
  const [currentLookAngle, setCurrentLookAngle] = useState(0);
  const [scores, setScores] = useState<ScoreWindow[]>([]);
  const [currentScore, setCurrentScore] = useState<ScoreWindow | null>(null);
  const [targetPlayer, setTargetPlayer] = useState<PlayerType | undefined>();

  useEffect(() => {
    setPos(getPointAtTime(points, props.time || 0, beziers).position);
  }, [props.time, points]);

  useEffect(() => {
    const angle = getAngleAtTime(lookAngles, state.time);
    setCurrentLookAngle(angle);
  }, [lookAngles, state.time]);

  useEffect(() => {
    const angle = getAngleAtTime(facingAngles, state.time);
    setCurrentFacingAngle(angle);
  }, [facingAngles, state.time]);

  useEffect(() => {
    if (!scores || scores.length === 0) {
      setCurrentScore(null);
      return;
    }

    const activeScore = scores.find(scoreWindow => state.time >= scoreWindow.t && state.time <= scoreWindow.endTime);
    setCurrentScore(activeScore || null);
  }, [scores, state.time]);

  const drawTargetPath = useCallback(
    (g: PixiGraphics) => {
      g.clear();
      if (!props.player || !targetPlayer) return;
      g.lineStyle(7 / state.mapScale, pressureColor, 0.7);
      g.moveTo(pos.x, pos.y);
      const playerPos = getPointAtTime(targetPlayer.points, state.time, targetPlayer.beziers).position;
      g.lineTo(playerPos.x, playerPos.y);
    },
    [pos, targetPlayer, state.time, state.mapScale]
  );

  useEffect(() => {
    // Set the target player if there is a current getball action
    const getBallAction = props.player.actions.find(
      (action) => action.type === ScenarioActionType.GetBall && action.startTime <= state.time && action.endTime >= state.time
    );
    if (getBallAction) {
      setTargetPlayer(getPlayer(state.clip, getBallAction.targetPlayer));
    } else {
      setTargetPlayer(undefined);
    }
  }, [props.player, state.time]);

  useEffect(() => {
    // Clone the points and beziers
    const newPoints: Waypoint[] = [];
    props.player.points.map((point) => newPoints.push({ ...point }));
    setPoints(newPoints);
    const newBeziers = getBeziers(newPoints);
    setBeziers(newBeziers);
    setTeamColour(props.player.team === 1 ? teamAColor : teamBColor);

    // Build face and look angle caches
    const faceActions = props.player.actions.filter(
      (action) => action.type === ScenarioActionType.FaceTarget || action.type === ScenarioActionType.FaceLocation
    );
    const lookActions = props.player.actions.filter((action) => action.type === ScenarioActionType.LookTarget);
    const newFaceAngles: Facing[] = [];
    const newLooks: Facing[] = [];

    for (let i = 0; i < newBeziers.length; i++) {
      const dist = newBeziers[i].length();
      const dt = angleCacheResolution / dist;
      for (let t = 0; t < 1; t += dt) {
        const currentPos = newBeziers[i].get(t);
        const fwd = newBeziers[i].derivative(t); // Gets the tangent
        const fwdAngle = (Math.atan2(fwd.y, fwd.x) + Math.PI * 1.5) % (Math.PI * 2);
        const nextTime = i >= newPoints.length - 1 ? state.clip.scenario.endTime : newPoints[i + 1].t;
        const time = lerp(newPoints[i].t, nextTime, t);
        let faceAngle = fwdAngle;
        let lookAngle = fwdAngle;

        // If there is a face action active at this time, override the face angle
        const activeFaceAction = faceActions.find((action) => action.startTime <= time && action.endTime >= time);
        if (activeFaceAction) {
          let targetPosition = activeFaceAction.targetPosition;
          if (activeFaceAction.type == ScenarioActionType.FaceTarget) {
            const targetPlayer = getPlayer(state.clip, activeFaceAction.targetPlayer);
            if (targetPlayer) targetPosition = getPointAtTime(targetPlayer.points, time, targetPlayer.beziers).position;
          }

          if (targetPosition) faceAngle = Math.atan2(targetPosition.y - currentPos.y, targetPosition.x - currentPos.x) + Math.PI * 1.5;
        }

        // If there is a look action active at this time, override the look angle
        const activeLookAction = lookActions.find((action) => action.startTime <= time && action.endTime >= time);
        if (activeLookAction) {
          const targetPlayer = getPlayer(state.clip, activeLookAction.targetPlayer);
          if (targetPlayer) {
            const targetPosition = getPointAtTime(targetPlayer.points, time, targetPlayer.beziers).position;
            lookAngle = Math.atan2(targetPosition.y - currentPos.y, targetPosition.x - currentPos.x) + Math.PI * 1.5;
          }
        }

        newFaceAngles.push({ angle: faceAngle, t: time });
        newLooks.push({ angle: lookAngle, t: time });
      }
    }

    setFacingAngles(newFaceAngles);
    setLookAngles(newLooks);

    // Build score cache
    const newScores: ScoreWindow[] = [];
    const scoreActions = props.player.actions
      .filter((action) => action.type === ScenarioActionType.Score)
      .sort((a, b) => a.startTime - b.startTime);
    for (let i = 0; i < scoreActions.length; i++) {
      newScores.push({
        score: scoreActions[i].index,
        t: scoreActions[i].startTime,
        time: scoreActions[i].time,
        endTime: scoreActions[i].endTime,
      });
    }
    setScores(newScores);
  }, [props.player]);

  useEffect(() => {
    setSelected(props.selected || false);
  }, [props.selected]);

  const handlePlayerClick = () => {
    // Clicking directly on a player when targeting a face action will create a FaceTarget action
    // (as opposed to clicking on the pitch, which creates a FaceLocation action)
    if (state.targetMode == TargetMode.Face) {
      const selectedPlayer = getSelectedPlayer(state);
      if (selectedPlayer) {
        const playerPosition = getPointAtTime(props.player.points, state.time, props.player.beziers).position;
        addAction(state, dispatch, selectedPlayer, ScenarioActionType.FaceTarget, props.player.id, playerPosition, state.time, state.time + 1);
      }

      dispatch({ targetMode: TargetMode.None });
      return;
    }

    if (state.targetMode != TargetMode.None) return;

    if (props.id == state.selectedPlayer) {
      const selectedPlayer = getSelectedPlayer(state);
      const playerPos = selectedPlayer ? getPointAtTime(selectedPlayer.points, state.time, selectedPlayer.beziers) : { position: { x: 0, y: 0 } };
      dispatch({
        selectedPosition: playerPos.position,
        radialAnchor: getClampedPosition(stageToScreen(playerPos.position, state), state.radialSize, state.field.mapWidth, state.field.mapHeight),
        showPlayerRadial: true,
        showActionRadial: false,
        showFieldRadial: false,
        showWaypointRadial: false,
      });
      return;
    } else {
      dispatch({
        selectedPlayer: props.id,
        selectedAction: -1,
      });
    }
  };

  const updateWaypoints = (waypoints: Waypoint[]) => {
    const player = getPlayer(state.clip, props.player.id);
    if (!player) return;
    const newPlayer = { ...player, id: props.player.id };
    newPlayer.points = [];
    newPlayer.beziers = getBeziers(waypoints);
    for (let i = 0; i < waypoints.length; i++) {
      newPlayer.points.push({ ...waypoints[i] });
    }

    updatePlayer(state, dispatch, newPlayer);
  };

  return (
    <>
      {state.options.pathLength > 0 && (
        <Route
          color={teamColour}
          points={points}
          selected={selected}
          alphaScale={state.options.pathLength}
          temporalAlpha
          onChange={updateWaypoints}
        />
      )}
      {state.options.showActions &&
        selected &&
        props.player.actions.map((action, actionIndex) => <PlayerAction playerId={props.id} action={action} actionIndex={actionIndex} size={45} />)}
      <Graphics draw={drawTargetPath} />
      <Container
        x={pos.x}
        y={pos.y}
        anchor={0.5}
        eventMode={state.targetMode == TargetMode.None || state.targetMode == TargetMode.Face ? "static" : "passive"}
        pointertap={handlePlayerClick}
        pointerover={() => {
          setHovered(true);
        }}
        pointerout={() => {
          setHovered(false);
        }}
      >
        <Sprite
          image={props.player.isPlayer ? Star : PlayerToken}
          width={(props.player.isPlayer ? size * 1.5 : size * 1.2) * state.spriteScale}
          height={(props.player.isPlayer ? size * 1.5 : size * 1.2 * 1.4) * state.spriteScale}
          tint={selected ? "white" : teamColour || "white"}
          alpha={hovered ? 0.85 : 1}
          anchor={0.5}
          rotation={props.player.isPlayer ? 0 : currentFacingAngle}
        />
        {!props.player.isPlayer && (
          <Sprite
            image={Chevron}
            width={size * 0.5 * state.spriteScale}
            height={size * 0.5 * state.spriteScale}
            tint={selected ? "white" : teamColour || "white"}
            alpha={hovered ? 0.85 : 1}
            anchor={{ x: 0.5, y: -1 }}
            rotation={currentLookAngle}
          />
        )}
        <Text
          eventMode="passive"
          text={
            (state.options.showJerseyNumbers
              ? props.player.jerseyNumber !== -1
                ? props.player.jerseyNumber?.toString()
                : "?"
              : props.player.initials) || "?"
          }
          anchor={0.5}
          x={0}
          y={0}
          rotation={-state.viewRotation}
          style={
            {
              align: "center",
              fill: "0x000000",
              fontSize: (state.options.showJerseyNumbers ? 18 : 14) * state.spriteScale,
              fontWeight: "bold",
              letterSpacing: 0,
            } as any
          }
        />
        {currentScore && (
          <Text
            eventMode="passive"
            text={`${currentScore.score} (${currentScore.time.toFixed(1)}s)`}
            anchor={0.5}
            x={0}
            y={size * -0.8 * state.spriteScale}
            style={
              {
                align: "center",
                fill: "white",
                fontSize: 16 * state.spriteScale,
                fontWeight: "bold",
                letterSpacing: 0,
              } as any
            }
          />
        )}
      </Container>
    </>
  );
};

export default Player;
